import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isHome = location.pathname === '/';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome
            ? 'bg-white/95 backdrop-blur-md border-b border-background-200'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link
              to="/"
              className="font-heading text-xl md:text-2xl font-bold text-foreground-950 cursor-pointer whitespace-nowrap hover:opacity-80 transition-opacity duration-150"
            >
              Superbuy Sheet
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`font-label text-sm font-medium transition-colors duration-150 cursor-pointer whitespace-nowrap ${
                  isHome ? 'text-foreground-950' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Home
              </Link>
              <Link
                to="/superbuy-spreadsheet-guide"
                className="font-label text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-150 cursor-pointer whitespace-nowrap"
              >
                Guide
              </Link>
              <Link
                to="/superbuy-spreadsheet-shoes"
                className="font-label text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-150 cursor-pointer whitespace-nowrap"
              >
                Shoes
              </Link>
              <Link
                to="/superbuy-spreadsheet-safety"
                className="font-label text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-150 cursor-pointer whitespace-nowrap"
              >
                Safety
              </Link>
              <Link
                to="/superbuy-spreadsheet-alternatives"
                className="font-label text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-150 cursor-pointer whitespace-nowrap"
              >
                Compare
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-foreground-950 text-background-50 rounded-lg font-label text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>

          <div className="absolute top-0 right-0 w-[85vw] max-w-sm h-full bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <span className="font-heading text-lg font-bold text-foreground-950">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="px-5 py-4 border-b border-background-100">
              <p className="font-label text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Navigate</p>
              <div className="space-y-1">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-700 font-label text-sm font-medium hover:bg-background-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer"
                >
                  <i className="ri-home-line text-lg"></i> Home
                </Link>
                <Link
                  to="/superbuy-spreadsheet-guide"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-700 font-label text-sm font-medium hover:bg-background-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer"
                >
                  <i className="ri-book-open-line text-lg"></i> Complete Guide
                </Link>
                <Link
                  to="/superbuy-spreadsheet-shoes"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-700 font-label text-sm font-medium hover:bg-background-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer"
                >
                  <i className="ri-footprint-line text-lg"></i> Best Shoes
                </Link>
                <Link
                  to="/superbuy-spreadsheet-safety"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-700 font-label text-sm font-medium hover:bg-background-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer"
                >
                  <i className="ri-shield-check-line text-lg"></i> Safety Guide
                </Link>
                <Link
                  to="/superbuy-spreadsheet-alternatives"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-700 font-label text-sm font-medium hover:bg-background-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer"
                >
                  <i className="ri-scales-line text-lg"></i> Compare
                </Link>
              </div>
            </div>

            <div className="px-5 py-4">
              <p className="font-label text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Shop Categories</p>
              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg text-foreground-700 font-label text-sm font-medium hover:bg-background-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-sm"></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-100">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-foreground-950 text-background-50 rounded-lg font-label text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.25s ease-out;
        }
      `}</style>
    </>
  );
}